import React from 'react';
import { ShippingRecord } from '../types';
import { useLanguage } from '../contexts/LanguageContext';
import { formatDate } from '../utils/dateFormatter';

interface ShippingRecordsListProps {
  records: ShippingRecord[];
  selectedRecordId: number | null;
  loading: boolean;
  analyzing: boolean;
  onSelectRecord: (id: number) => void;
}

const ShippingRecordsList: React.FC<ShippingRecordsListProps> = ({
  records,
  selectedRecordId,
  loading,
  analyzing,
  onSelectRecord
}) => {
  const { t } = useLanguage();
  
  // Get a shortened address (first 50 characters)
  const shortenAddress = (address: string) => {
    if (address.length > 50) {
      return address.substring(0, 50) + '...';
    }
    return address;
  };

  return (
    <div className="card shadow-sm h-100">
      <div className="card-header bg-primary text-white">
<div className="d-flex justify-content-between align-items-center">
          <h5 className="mb-0">
     <i className="bi bi-truck me-2"></i>
       {t('shipping.records')}
  </h5>
     <span className="badge bg-light text-primary rounded-pill px-3">
    {records.length}
   </span>
        </div>
      </div>
      <div className="card-body p-0" style={{ maxHeight: '600px', overflowY: 'auto' }}>
   {loading ? (
          <div className="text-center p-5">
        <div className="spinner-border text-primary mb-3" role="status" style={{ width: '3rem', height: '3rem' }}>
    <span className="visually-hidden">{t('loading')}</span>
       </div>
    <p className="text-muted">{t('loading.shipping.records')}</p>
     </div>
   ) : records.length === 0 ? (
          <div className="p-5 text-center text-muted">
     <i className="bi bi-inbox d-block mb-3 text-secondary" style={{ fontSize: '64px', opacity: 0.3 }}></i>
    <h6 className="text-muted">{t('no.shipping.records.found')}</h6>
          <p className="small">{t('try.adjusting.filters')}</p>
    </div>
        ) : (
          <div className="list-group list-group-flush">
    {records.map(record => (
   <div
   key={record.id}
     className={`list-group-item list-group-item-action border-start-0 border-end-0 ${
 selectedRecordId === record.id ? 'active' : ''
      }`}
                style={{ 
         cursor: 'pointer',
        transition: 'all 0.2s ease',
       borderLeft: selectedRecordId === record.id ? '4px solid #0d6efd' : '4px solid transparent',
     backgroundColor: selectedRecordId === record.id ? '#e7f1ff' : 'transparent'
  }}
     onClick={() => onSelectRecord(record.id)}
     >
     <div className="d-flex justify-content-between align-items-start">
        <div className="flex-grow-1">
          {/* License Plate */}
             <div className="d-flex align-items-center mb-2">
       <i className="bi bi-truck-front me-2 text-primary" style={{ fontSize: '1.2rem' }}></i>
            <h6 className={`mb-0 fw-bold ${selectedRecordId === record.id ? 'text-primary' : ''}`}>
             {record.licensePlate}
           </h6>
   </div>

        {/* Shipping Date */}
 <div className="d-flex align-items-center mb-2">
          <i className="bi bi-calendar-event me-2 text-muted" style={{ fontSize: '0.9rem' }}></i>
      <small className={selectedRecordId === record.id ? 'text-primary' : 'text-muted'}>
   {formatDate(record.shippingDate)}
   </small>
      </div>

 {/* Shipping Address */}
         <div className="d-flex align-items-start">
         <i className="bi bi-geo-alt me-2 text-muted" style={{ fontSize: '0.9rem', marginTop: '2px' }}></i>
             <small 
  className={selectedRecordId === record.id ? 'text-primary' : 'text-muted'}
         title={record.shippingAddress}
 >
          {shortenAddress(record.shippingAddress)}
          </small>
      </div>

      {/* Vehicle ID Badge */}
        {record.vehicleId && (
            <div className="mt-2">
 
   </div>
       )}
  </div>

             {/* Loading Spinner or Arrow Icon */}
  <div className="ms-3 d-flex align-items-center">
         {analyzing && selectedRecordId === record.id ? (
    <div className="spinner-border spinner-border-sm text-primary" role="status">
       <span className="visually-hidden">{t('analyzing')}</span>
         </div>
  ) : selectedRecordId === record.id ? (
           <i className="bi bi-chevron-right text-primary" style={{ fontSize: '1.2rem' }}></i>
     ) : (
   <i className="bi bi-chevron-right text-muted" style={{ fontSize: '1rem', opacity: 0.3 }}></i>
   )}
              </div>
       </div>

   {/* Selected Indicator */}
          {selectedRecordId === record.id && (
     <div className="mt-2 pt-2 border-top">
        <small className="text-primary">
       <i className="bi bi-check-circle-fill me-1"></i>
             {t('currently.analyzing')}
      </small>
  </div>
       )}
     </div>
   ))}
 </div>
   )}
      </div>

      {/* Footer with helpful info */}
      {!loading && records.length > 0 && (
   <div className="card-footer bg-light text-muted small">
    <i className="bi bi-info-circle me-1"></i>
{t('click.record.to.view.analysis')}
</div>
      )}
    </div>
  );
};

export default ShippingRecordsList;
